import type { FC } from "react";

import { Select } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";

import AffiliateKpisOverview, {
  type AffiliateKpisInput,
} from "./AffiliateKpisOverview";

import styles from "./AffiliateKpis.module.css";

// Re-export so existing callers (Affiliates.tsx, tests) keep importing
// the input type from the same module they already do.
export type { AffiliateKpisInput };

// One dropdown option row (id → label).
export type AffiliateKpisFilterOption = { value: string; label: string };

export type AffiliateKpisProps = {
  // Raw `portfolio_kpis.kpis` block from the affiliates list response.
  // Forwarded to `AffiliateKpisOverview`, which owns the tile catalogue.
  kpis: AffiliateKpisInput;
  isLoading: boolean;
  // Controls the collapsible Affiliates + Code filters panel. The toggle
  // button + date picker + download button live in the parent (next to the
  // tab strip) so they sit at the same visual level as the tabs; the panel
  // stays here because it's tab-scoped content.
  showFilters: boolean;
  // Controlled: parent (`Affiliates`) owns the picked affiliate + code so
  // both the panel and the downstream charts read from a single source.
  selectedAffiliate?: string;
  onAffiliateChange: (next: string | undefined) => void;
  affiliateOptions: ReadonlyArray<AffiliateKpisFilterOption>;
  isLoadingAffiliates: boolean;
  selectedCode?: string;
  onCodeChange: (next: string | undefined) => void;
  codeOptions: ReadonlyArray<AffiliateKpisFilterOption>;
  isLoadingCodes: boolean;
};

/**
 * LIST-page KPIs tab. Renders the collapsible Affiliates + Code filters
 * panel + the cards / charts overview. The header row (filter toggle,
 * date picker, download button) is owned by the parent (`Affiliates`)
 * so it sits inline with the tab strip.
 */
const AffiliateKpis: FC<AffiliateKpisProps> = ({
  kpis,
  isLoading,
  showFilters,
  selectedAffiliate,
  onAffiliateChange,
  affiliateOptions,
  isLoadingAffiliates,
  selectedCode,
  onCodeChange,
  codeOptions,
  isLoadingCodes,
}) => {
  return (
    <>
      <div
        className={styles.filtersPanel}
        data-testid="affiliate-kpis-filters-panel"
        style={{ display: showFilters ? "grid" : "none" }}
      >
        <Select<string>
          className={styles.filterSelect}
          placeholder={AppStrings.AFFILIATES}
          value={selectedAffiliate}
          onChange={(value) => onAffiliateChange(value)}
          allowClear
          showSearch
          optionFilterProp="label"
          options={affiliateOptions as { value: string; label: string }[]}
          loading={isLoadingAffiliates}
        />
        <Select<string>
          className={styles.filterSelect}
          placeholder={AppStrings.CODE}
          value={selectedCode}
          onChange={(value) => onCodeChange(value)}
          allowClear
          showSearch
          optionFilterProp="label"
          options={codeOptions as { value: string; label: string }[]}
          loading={isLoadingCodes}
          // Code lookup is scoped to whichever affiliate the operator
          // picked — surface that in the placeholder when nothing is
          // selected yet so the empty state doesn't look broken.
          disabled={!selectedAffiliate}
        />
      </div>

      <AffiliateKpisOverview
        kpis={kpis}
        isLoading={isLoading}
        affiliate={selectedAffiliate}
        promoCode={selectedCode}
        affiliateOptions={affiliateOptions}
        isLoadingAffiliates={isLoadingAffiliates}
        codeOptions={codeOptions}
        isLoadingCodes={isLoadingCodes}
      />
    </>
  );
};

export default AffiliateKpis;
